import React, { useState } from "react";
import { Grid, Typography, LinearProgress, Box, Card, Collapse, IconButton, Tooltip } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WarningIcon from "@material-ui/icons/Warning";
import SameApplicantTable from "./sameApplicantTable/SameApplicantTable";

interface RelatedApplicationsSummaryData {
  approved: number;
  rejected: number;
  review: number;
  suspended: number;
  approved_for_applicant?: number;
  review_for_applicant?: number;
}

interface RelatedApplication {
  _id: string;
  loan_information?: { loan_id?: string; amount?: number };
  approved?: boolean;
  suspended?: boolean;
  submit_date?: string;
  borrower_information?: { borrower?: { private_number?: string; name?: string } };
  program_type?: any;
  application_type?: any;
  source_model: "mortgage" | "new_universal" | "universal";
}

interface RelatedApplicationsSummaryProps {
  summaryData: RelatedApplicationsSummaryData;
  applicationsData: RelatedApplication[];
  maxValue?: number;
  currentApplicationStatus?: "approved" | "rejected" | "review" | "suspended";
  component?: "mortgage" | "new_universal";
}

const formatNumber = (num?: number) => num?.toLocaleString() ?? "0";

const ExpandMore: React.FC<{ expand: boolean } & React.ComponentProps<typeof IconButton>> = ({ expand, style, ...props }) => (
  <IconButton
    {...props}
    style={{
      transform: expand ? "rotate(180deg)" : "rotate(0deg)",
      marginLeft: "auto",
      transition: "transform 0.3s",
      ...style,
    }}
  />
);

const getProgressValue = (amount: number, max: number) => Math.min((amount / max) * 100, 100);

const isOverLimit = (amount: number, max: number) => (amount / max) * 100 > 100;

const getWarningStyles = (amount: number, max: number) =>
  isOverLimit(amount, max)
    ? {
        border: "2px solid #ff5722",
        boxShadow: "0 0 10px rgba(255, 87, 34, 0.3)",
      }
    : {};

const formatPercentageDisplay = (amount: number, max: number) => {
  const percent = (amount / max) * 100;
  if (percent > 1000) return "";
  return `${percent > 100 ? percent.toFixed(1) : percent.toFixed(2)}% of ${formatNumber(max)}`;
};

const statusMeta = {
  approved: { label: "დადასტურებული", color: "green", bg: "#e8f5e9", bgBar: "#c8e6c9" },
  review: { label: "განსახილველი", color: "gray", bg: "#f5f5f5", bgBar: "#e0e0e0" },
  suspended: { label: "შეჩერებული", color: "orange", bg: "#fff3e0", bgBar: "#ffe0b2" },
  rejected: { label: "უარყოფილი", color: "red", bg: "#ffebee", bgBar: "#ffcdd2" },
  approvedAndReview: {
    label: "დადასტურებული + განსახილველი",
    color: "#1976d2",
    bg: "#e3f2fd",
    bgBar: "#bbdefb",
  },
  approvedAndReviewForApplicant: {
    label: "დადასტურებული + განსახილველი (ბენეფიციარისთვის)",
    color: "#7b1fa2",
    bg: "#f3e5f5",
    bgBar: "#ce93d8",
  },
} as const;

const RelatedApplicationsSummary: React.FC<RelatedApplicationsSummaryProps> = ({
  summaryData,
  applicationsData,
  maxValue: propMaxValue,
  currentApplicationStatus = "review",
  component = "mortgage",
}) => {
  const [expanded, setExpanded] = useState(false);
  const baseLoanAmount = propMaxValue || 20_000_000;

  const summary = {
    approved: summaryData.approved,
    review: summaryData.review,
    suspended: summaryData.suspended,
    rejected: summaryData.rejected,
  };
  const approvedAndReview = summary.approved + summary.review;

  // Calculate applicant-only amounts (backend already includes current application)
  const approvedForApplicant = summaryData.approved_for_applicant || 0;
  const reviewForApplicant = summaryData.review_for_applicant || 0;
  const approvedAndReviewForApplicant = approvedForApplicant + reviewForApplicant;

  const getMax = (key: keyof typeof summary | "approvedAndReview" | "approvedAndReviewForApplicant") => {
    if (key === "approvedAndReviewForApplicant") {
      return ["approved", "review"].includes(currentApplicationStatus) ? 10_000_000 : 1;
    }
    return key === currentApplicationStatus || (key === "approvedAndReview" && ["approved", "review"].includes(currentApplicationStatus)) ? baseLoanAmount : 1;
  };

  const renderSummaryCard = (key: keyof typeof statusMeta, value: number) => {
    const max = getMax(key as any);
    const meta = statusMeta[key];
    const overLimit = isOverLimit(value, max);
    return (
      <Card elevation={0} style={{ padding: 16, backgroundColor: meta.bg, ...getWarningStyles(value, max) }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" style={{ color: meta.color }}>
            {meta.label}
          </Typography>
          {overLimit && (
            <Tooltip title="გაფრთხილება: თანხა აღემატება ლიმიტს!">
              <WarningIcon style={{ color: "#ff5722", fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          {formatNumber(value)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={getProgressValue(value, max)}
          style={{ marginTop: 8, backgroundColor: meta.bgBar }}
          color={overLimit ? "secondary" : "primary"}
        />
        <Typography variant="caption" display="block">
          {formatPercentageDisplay(value, max)}
        </Typography>
      </Card>
    );
  };

  const getVisibleStatusKeys = () => {
    const allKeys = ["approved", "review", "suspended", "rejected"] as const;
    if (component === "new_universal") {
      return allKeys.filter((key) => key !== "suspended" && key !== "rejected");
    }
    return allKeys;
  };

  const getGridColumnSize = () => {
    const visibleKeys = getVisibleStatusKeys();
    const count = visibleKeys.length;

    switch (count) {
      case 1:
        return { xs: 12 as const, sm: 12 as const, md: 12 as const };
      case 2:
        return { xs: 12 as const, sm: 6 as const, md: 6 as const };
      case 3:
        return { xs: 12 as const, sm: 6 as const, md: 4 as const };
      case 4:
      default:
        return { xs: 12 as const, sm: 6 as const, md: 3 as const };
    }
  };

  const hasSummary = Object.values(summary).some(Boolean);
  const hasApplications = applicationsData.length > 0;

  if (!hasSummary && !hasApplications) return null;

  return (
    <Card elevation={0} style={{ padding: 16, marginBottom: 16 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ margin: 16, marginBottom: hasSummary ? 16 : 0 }}>
        <Typography variant="h6">დაკავშირებული განაცხადების ჯამური მონაცემები</Typography>
      </Box>

      {hasSummary && (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          {getVisibleStatusKeys().map((key) => {
            const gridProps = getGridColumnSize();
            return (
              <Grid item {...gridProps} key={key}>
                {renderSummaryCard(key, summary[key])}
              </Grid>
            );
          })}
          <Grid item xs={12} md={component === "new_universal" ? 6 : 12}>
            {renderSummaryCard("approvedAndReview", approvedAndReview)}
          </Grid>
          {component === "new_universal" && (
            <Grid item xs={12} md={6}>
              {renderSummaryCard("approvedAndReviewForApplicant", approvedAndReviewForApplicant)}
            </Grid>
          )}
        </Grid>
      )}

      {hasApplications && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: "pointer", marginTop: 16, padding: 8, borderTop: "1px solid #eee" }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
              დაკავშირებული განაცხადები
            </Typography>
            <Tooltip title={expanded ? "ცხრილის დამალვა" : "ცხრილის ჩვენება"}>
              <ExpandMore expand={expanded} aria-expanded={expanded} aria-label="show table">
                <ExpandMoreIcon />
              </ExpandMore>
            </Tooltip>
          </Box>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box style={{ marginTop: 8 }}>
              <SameApplicantTable rows={applicationsData} component={component} classes={{}} />
            </Box>
          </Collapse>
        </>
      )}
    </Card>
  );
};

export default RelatedApplicationsSummary;
